/* eslint-disable security/detect-object-injection */
import {fromCallback} from 'xstate';

import {UseFormReturn} from 'react-hook-form';

import {BaseVehicle} from '../../types/BaseVehicle';
import {VEHICLE_FORM_EVENTS} from '../constants/VEHICLE_FORM_EVENTS';
import {FieldChangedEvent, StopWatchingEvent, TypeFieldChangedEvent} from '../types/events';

export interface FieldWatcherInput {
  form: UseFormReturn<BaseVehicle>;
  debounceMs?: number;
}

export interface TypeWatcherInput extends FieldWatcherInput {
  getCatalogMakesForType?: (vehicleType: string) => Promise<string[]>;
  getCatalogModelsForTypeAndMake?: (vehicleType: string, make: string) => Promise<string[]>;
}

type EventType = keyof typeof VEHICLE_FORM_EVENTS;

type WatcherEvents = FieldChangedEvent<EventType> | StopWatchingEvent;

type FieldNames = keyof BaseVehicle | Array<keyof BaseVehicle>;

/**
 * Clears all timers in the timers map
 */
const clearAllTimers = (timers: Map<string, number>): void => {
  timers.forEach((timerId) => window.clearTimeout(timerId));
  timers.clear();
};

/**
 * Normalizes field names input to always return an array
 */
const normalizeFields = (fields: FieldNames): Array<keyof BaseVehicle> =>
  Array.isArray(fields) ? fields : [fields];

/**
 * Creates a field watcher actor that monitors form field changes
 * Optimized for XState actor patterns with functional approach
 *
 * @param fields - Field name(s) to watch from BaseVehicle type
 * @param eventType - The event type to send (e.g., 'MAKE_FIELD_CHANGED')
 * @returns XState callback actor that emits field change events
 *
 * @example
 * ```typescript
 * // Single field with custom event type
 * const makeWatcher = createFieldWatcher('formMake', 'MAKE_FIELD_CHANGED');
 *
 * // Multiple fields
 * const modelWatcher = createFieldWatcher(['formMake', 'formModelFamily'], 'MODEL_FIELD_CHANGED');
 * ```
 */
export const createFieldWatcher = (fields: FieldNames, eventType: EventType) =>
  fromCallback<FieldChangedEvent<EventType>, FieldWatcherInput>(({input, sendBack, receive}) => {
    const {form, debounceMs = 300} = input;
    const watchFields = normalizeFields(fields);

    const isActiveRef = {current: true};
    const previousValues = new Map<string, unknown>();
    const debounceTimers = new Map<string, number>();

    for (const fieldName of watchFields) {
      previousValues.set(fieldName as string, form.getValues(fieldName));
    }

    const unsubscribe = form.subscribe({
      name: watchFields,
      exact: true,
      formState: {
        values: true,
      },
      callback: (data) => {
        if (!isActiveRef.current) {
          return;
        }

        for (const fieldName of watchFields) {
          const fieldKey = fieldName as string;
          const currentValue = data.values[fieldName];
          const previousValue = previousValues.get(fieldKey);

          if (currentValue === previousValue) {
            continue;
          }

          const existingTimer = debounceTimers.get(fieldKey);
          if (existingTimer !== undefined) {
            window.clearTimeout(existingTimer);
          }

          const timerId = window.setTimeout(() => {
            if (isActiveRef.current) {
              sendBack({
                type: eventType,
              } satisfies FieldChangedEvent<EventType>);
              previousValues.set(fieldKey, currentValue);
            }
            debounceTimers.delete(fieldKey);
          }, debounceMs);

          debounceTimers.set(fieldKey, timerId);
        }
      },
    });

    // Handle actor commands
    receive((event: WatcherEvents) => {
      if (event.type === VEHICLE_FORM_EVENTS.STOP_WATCHING) {
        isActiveRef.current = false;
        clearAllTimers(debounceTimers);
        unsubscribe();
      }
    });

    return () => {
      isActiveRef.current = false;
      clearAllTimers(debounceTimers);
      unsubscribe();
    };
  });

/**
 * Specialised type watcher actor that enriches TYPE_FIELD_CHANGED with catalog lookup results.
 *
 * Before sending TYPE_FIELD_CHANGED, it:
 * 1. Reads the current make and model values from the form
 * 2. Calls getCatalogMakesForType(newType) to check if the current make exists in the new type
 * 3. If make is preserved, calls getCatalogModelsForTypeAndMake(newType, make) to check the model
 * 4. Resolves makeExistsInNewType and modelExistsInNewType flags and includes them in the event payload
 *
 * This allows the machine action to decide whether to preserve or clear make/model fields
 * without any async logic inside the machine itself.
 *
 * Race condition safety: a monotonically-increasing generation counter ensures that
 * if the type field changes again while a catalog lookup is in-flight, the stale
 * result is discarded and only the most-recent lookup's result is sent.
 */
const typeWatcherActor = fromCallback<TypeFieldChangedEvent, TypeWatcherInput>(
  ({input, sendBack, receive}) => {
    let isActive = true;
    let previousValue: unknown = input.form.getValues('type');
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    // Monotonically-increasing counter used to discard stale async results when
    // the type field changes again before a previous catalog lookup resolves.
    let currentGeneration = 0;

    const unsubscribe = input.form.subscribe({
      name: 'type',
      exact: true,
      formState: {values: true},
      callback: (data) => {
        if (!isActive) {
          return;
        }

        const currentValue = data.values['type'];

        // Always cancel any pending debounce and advance the generation first, so that
        // in-flight lookups from a prior change are invalidated even when the value
        // reverts to previousValue.
        if (debounceTimer !== undefined) {
          clearTimeout(debounceTimer);
          debounceTimer = undefined;
        }

        // Advance generation to invalidate any in-flight lookup from a prior type change.
        currentGeneration += 1;
        const generation = currentGeneration;

        if (currentValue === previousValue) {
          return;
        }

        debounceTimer = setTimeout(async () => {
          if (!isActive) {
            return;
          }

          const newType = Array.isArray(currentValue) ? currentValue[0] : currentValue;
          // For catalog selections only formMake is written; `make` may lag behind.
          // Prefer `make` (canonical API value) but fall back to `formMake` (UI value).
          // Both hold the same raw catalog make string (e.g. "VOLKSWAGEN").
          const currentMake =
            input.form.getValues('make') ?? input.form.getValues('formMake') ?? null;
          // Same pattern for model: prefer canonical `modelFamily`, fall back to `formModelFamily`.
          const currentModel =
            input.form.getValues('modelFamily') ?? input.form.getValues('formModelFamily') ?? null;

          let makeExistsInNewType: boolean | null = null;
          let modelExistsInNewType: boolean | null = null;

          if (newType && currentMake && input.getCatalogMakesForType) {
            try {
              const catalogMakes = await input.getCatalogMakesForType(newType);

              // Guard against stale results: discard if the type changed again while
              // the lookup was in-flight, or if the actor was stopped.
              if (!isActive || generation !== currentGeneration) {
                return;
              }

              makeExistsInNewType = catalogMakes.includes(currentMake);
            } catch {
              // If lookup fails, default to null (safe fallback: clear make)
              makeExistsInNewType = null;

              if (!isActive || generation !== currentGeneration) {
                return;
              }
            }
          }

          // Only look up the model when make is preserved — model lookup is only meaningful
          // when we know the make will stay, because models are type+make specific.
          if (
            makeExistsInNewType === true &&
            newType &&
            currentMake &&
            currentModel &&
            input.getCatalogModelsForTypeAndMake
          ) {
            try {
              const catalogModels = await input.getCatalogModelsForTypeAndMake(
                newType,
                currentMake
              );

              if (!isActive || generation !== currentGeneration) {
                return;
              }

              modelExistsInNewType = catalogModels.includes(currentModel);
            } catch {
              // If model lookup fails, default to null (safe fallback: clear model)
              modelExistsInNewType = null;

              if (!isActive || generation !== currentGeneration) {
                return;
              }
            }
          }

          if (isActive && generation === currentGeneration) {
            // Only advance previousValue and clear timer here — after all guards pass —
            // so that a stale early-return does not silently consume the change marker.
            // If we returned early above, previousValue stays at its old value and the
            // next identical change from the form will still be treated as a real change.
            previousValue = currentValue;
            debounceTimer = undefined;
            sendBack({
              type: VEHICLE_FORM_EVENTS.TYPE_FIELD_CHANGED,
              makeExistsInNewType,
              modelExistsInNewType,
            });
          }
        }, input.debounceMs ?? 300);
      },
    });

    receive((event: WatcherEvents) => {
      if (event.type === VEHICLE_FORM_EVENTS.STOP_WATCHING) {
        isActive = false;
        if (debounceTimer !== undefined) {
          clearTimeout(debounceTimer);
        }
        unsubscribe();
      }
    });

    return () => {
      isActive = false;
      if (debounceTimer !== undefined) {
        clearTimeout(debounceTimer);
      }
      unsubscribe();
    };
  }
);

export const fieldWatchersActors = {
  /** Watches the type field — enriches the event with catalog lookup result */
  typeWatcherActor,
  /** Watches the formMake field */
  makeWatcherActor: createFieldWatcher('formMake', VEHICLE_FORM_EVENTS.MAKE_FIELD_CHANGED),
  /** Watches the formModelFamily field */
  modelWatcherActor: createFieldWatcher('formModelFamily', VEHICLE_FORM_EVENTS.MODEL_FIELD_CHANGED),
  /** Watches the fuelType and hybridType fields */
  fuelTypeWatcherActor: createFieldWatcher(
    ['fuelType', 'hybridType'],
    VEHICLE_FORM_EVENTS.FUEL_TYPE_CHANGED
  ),
} as const;
